// Draft of the lobby content page. The draft is the operator's work: only a save he A accepted
// clears it. A refused save (validation, another operator saved first, he A down, command
// failed) keeps it, in memory and in localStorage, so a reload does not lose it either.

export type Draft<D> = { doc: D; version: number; savedAt: number };

export type SaveOutcome =
  | { kind: 'saved' }
  | { kind: 'stale'; serverVersion: number }
  | { kind: 'refused'; code: string }
  | { kind: 'pending'; code: string };

const KEY = 'cms.content.draft';

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
const store = (): Store | null => (typeof localStorage === 'undefined' ? null : localStorage);

export function loadDraft<D>(s: Store | null = store()): Draft<D> | null {
  try {
    const raw = s?.getItem(KEY);
    if (!raw) return null;
    const d = JSON.parse(raw) as Draft<D>;
    return d && typeof d === 'object' && Number.isInteger(d.version) && d.doc && typeof d.doc === 'object' ? d : null;
  } catch {
    return null;
  }
}

export function keepDraft<D>(d: Draft<D> | null, s: Store | null = store()) {
  try {
    if (d) s?.setItem(KEY, JSON.stringify(d));
    else s?.removeItem(KEY);
  } catch {
    // storage full or blocked: the in-memory draft still holds
  }
}

/** The draft after a save attempt: cleared only when he A stored it. */
export function afterSave<D>(d: Draft<D> | null, o: SaveOutcome): Draft<D> | null {
  return o.kind === 'saved' ? null : d;
}

/** Take the server's newer version as the base of the draft, keeping every edit (overwrite on next save). */
export function rebase<D>(d: Draft<D>, serverVersion: number): Draft<D> {
  return { ...d, version: serverVersion };
}
